import React, { useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { REVIEWED_WITH_MAX } from '@marketing-milk/interfaces'
import { dark, radius, type as typo } from '../tokens'

/**
 * Who the employee is reviewing with, for this visit. `asking` shows the form,
 * `named` the chip, and `none` nothing — after Skip or ×, verdicts record exactly
 * as they did before this existed.
 *
 * `asking` reopened from Change carries the name it was opened over in `keeping`,
 * so fixing a typo mid-review doesn't drop the contact from every verdict until
 * the form is saved. Only Skip or × let go of it.
 */
export type ReviewingWithState =
  | { kind: 'asking'; draft: string; keeping?: string }
  | { kind: 'named'; name: string }
  | { kind: 'none' }

/** The name verdicts carry right now, if any. */
export const activeReviewedWith = (state: ReviewingWithState): string | null =>
  state.kind === 'named' ? state.name : state.kind === 'asking' ? state.keeping ?? null : null

interface Props {
  state: ReviewingWithState
  onChange: (next: ReviewingWithState) => void
}

/**
 * The employee-only "Who are you reviewing with?" prompt and the chip it turns
 * into (DRI-161). Inline above the filters rather than a pop-up, so the page is
 * usable around it — an employee who ignores it can review straight away.
 *
 * The name lives only in the verdict's audit comment, so nothing here persists:
 * a reload asks again, which is what "for this visit" means.
 */
export function ReviewingWith({ state, onChange }: Props) {
  if (state.kind === 'none') return null
  if (state.kind === 'named') {
    return (
      <div style={{ display: 'flex', marginTop: 32 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '5px 6px 5px 12px',
            borderRadius: 999,
            border: `1px solid ${dark.hairline}`,
            background: dark.surface,
            fontFamily: typo.family.sans,
            fontSize: typo.size.body,
            color: dark.text,
            maxWidth: '100%',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Reviewing with <b style={{ fontWeight: typo.weight.semibold }}>{state.name}</b>
          </span>
          <span aria-hidden="true" style={{ color: dark.textFaint }}>
            ·
          </span>
          <button
            type="button"
            onClick={() => onChange({ kind: 'asking', draft: state.name, keeping: state.name })}
            style={{
              border: 0,
              background: 'none',
              padding: 0,
              fontFamily: typo.family.sans,
              fontSize: typo.size.body,
              fontWeight: typo.weight.semibold,
              color: dark.accent,
              cursor: 'pointer',
            }}
          >
            Change
          </button>
          <button
            type="button"
            aria-label="Stop recording who you're reviewing with"
            onClick={() => onChange({ kind: 'none' })}
            style={{
              width: 24,
              height: 24,
              flexShrink: 0,
              borderRadius: '50%',
              border: 0,
              padding: 0,
              background: dark.hairline,
              color: dark.textMuted,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <CloseIcon style={{ fontSize: 14 }} aria-hidden="true" />
          </button>
        </span>
      </div>
    )
  }

  return <ReviewingWithForm initial={state.draft} onChange={onChange} />
}

function ReviewingWithForm({
  initial,
  onChange,
}: {
  initial: string
  onChange: (next: ReviewingWithState) => void
}) {
  const [draft, setDraft] = useState(initial)
  const name = draft.replace(/\s+/g, ' ').trim()

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        if (name) onChange({ kind: 'named', name })
      }}
      style={{
        marginTop: 32,
        border: `1px solid ${dark.hairline}`,
        borderRadius: radius.md,
        background: dark.surface,
        padding: 16,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: 12,
      }}
    >
      <label
        style={{
          flex: '1 1 320px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          fontFamily: typo.family.sans,
          fontSize: typo.size.body,
          fontWeight: typo.weight.semibold,
          color: dark.text,
        }}
      >
        Who are you reviewing with?
        <input
          type="text"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          maxLength={REVIEWED_WITH_MAX}
          placeholder="Client contact's name"
          style={{
            fontFamily: typo.family.sans,
            fontWeight: typo.weight.regular,
            fontSize: typo.size.body,
            padding: '8px 10px',
            borderRadius: radius.sm,
            border: `1px solid ${dark.hairline}`,
            background: dark.bg,
            color: dark.text,
          }}
        />
      </label>
      <button
        type="submit"
        disabled={!name}
        style={{
          ...button,
          border: 0,
          background: dark.accent,
          color: dark.accentInk,
          cursor: name ? 'pointer' : 'default',
          opacity: name ? 1 : 0.5,
        }}
      >
        Start reviewing
      </button>
      <button
        type="button"
        onClick={() => onChange({ kind: 'none' })}
        style={{
          ...button,
          border: `1px solid ${dark.hairline}`,
          background: 'transparent',
          color: dark.textMuted,
          cursor: 'pointer',
        }}
      >
        Skip
      </button>
    </form>
  )
}

const button: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: radius.sm,
  fontFamily: typo.family.sans,
  fontSize: typo.size.body,
  fontWeight: typo.weight.semibold,
}
